import { Component, inject, input, signal, computed, effect } from '@angular/core';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { AssetsService } from '../../services/asset.service';
import { Sensor } from '../../models/classes/sensor';
import { SensorAdditionalField } from '../../models/classes/sensoradditionalfield';
import { IHtstInfoField } from '../../models/sensoradditionalfield.model';
import { SpinnerComponent } from '../spinner/spinner';
import { NzListModule } from 'ng-zorro-antd/list';
import { CardComponent } from '../card/card';
import { HtstInfoDataComponent } from './htst-information/htst-info';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';

@Component({
  selector: 'app-sensor-installation-data',
  templateUrl: './sensor-installation-data.html',
  styleUrls: ['./sensor-installation-data.scss'],
  imports: [SpinnerComponent, NzListModule, CardComponent, HtstInfoDataComponent],
})
export class SensorInstallationDataComponent {
  //Input
  sensor = input<Sensor | null>(null);

  //Inject services
  private assetsService = inject(AssetsService);
  public i18n = inject(I18nService);
  private ui = inject(UiService);

  //Signals for state
  sensorAdditionalInfo = signal<SensorAdditionalField[]>([]);
  installationDate = signal<string | null>(null);
  inletHtstInfo = signal<IHtstInfoField>({});
  outletHtstInfo = signal<IHtstInfoField>({});
  isHtstSensor = signal<boolean>(false);

  //Computed for UI responsiveness
  isMobile = computed(() => this.ui.IsMobile());

  // Effect: Load additional info when sensor changes
  private loadEffect = effect(() => {
    const s = this.sensor();
    if (!s) return;

    if (s.isStreamTrap || s.isRotaingEquipment) {
      this.assetsService.getSensorAdditionalInfo(s.sensorID).then((res) => {
        this.sensorAdditionalInfo.set(res);
        this.processSensorAdditionalInfo();
      });
    }
  });

  // Optimized processSensorAdditionalInfo
  private processSensorAdditionalInfo() {
    const info = this.sensorAdditionalInfo();
    if (!info || info.length <= 10) return;

    const findInfo = (category: string) => info.find((elm) => elm.category === category)?.value;

    // Define mappings for inlet and outlet
    const inletMap = {
      gatewayId: 'Inlet-Gateway',
      macAddress: 'Inlet-MAC',
      dauModel: 'Inlet-Model',
      sensorColor: 'Inlet-ColourCodeHex',
    };

    const outletMap = {
      gatewayId: 'Outlet-Gateway',
      macAddress: 'Outlet-MAC',
      dauModel: 'Outlet-Model',
      sensorColor: 'Outlet-ColourCodeHex',
    };

    // Helper to build HTST info object

    const buildHtstInfo = (map: Record<keyof IHtstInfoField, string>): IHtstInfoField => {
      const result: IHtstInfoField = {};
      for (const [key, category] of Object.entries(map) as [keyof IHtstInfoField, string][]) {
        const value = findInfo(category);
        if (!value) continue;

        result[key] = key === 'macAddress' ? value.match(/.{1,2}/g)?.join(':') : value;
      }

      return Object.keys(result).length ? result : {};
    };

    this.installationDate.set(findInfo('Sensor-CreationDate') ?? null);

    const inletInfo = buildHtstInfo(inletMap);
    const outletInfo = buildHtstInfo(outletMap);

    if (inletInfo) {
      this.isHtstSensor.set(true);
      this.inletHtstInfo.set(inletInfo);
    }

    if (outletInfo) {
      this.outletHtstInfo.set(outletInfo);
    }
  }

  //Optimized InstallationData using dynamic mapping
  InstallationData = computed(() => {
    const s = this.sensor();
    const info = this.sensorAdditionalInfo();
    if (!s) return null;

    const findInfo = (category: string) => info.find((elm) => elm.category === category)?.value;

    const config = [
      {
        condition: !!s.sensorName && !s.isRotaingEquipment,
        headerKey: 'sensor_name_install_data',
        categoryKey: 'sensor_name_install_data',
        value: () => s.sensorName,
      },
      {
        condition: !!s.sensorName && s.isRotaingEquipment,
        headerKey: 'add_equipment_name',
        categoryKey: 'add_equipment_name',
        value: () => s.sensorName,
      },
      {
        condition: s.isRotaingEquipment && info.length > GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
        headerKey: 'plant_name',
        categoryKey: 'plant_name',
        value: () => findInfo('PlantName'),
      },
      {
        condition: !!s.sensorDigits && !s.isStreamTrap && !s.isRotaingEquipment,
        headerKey: 'sensor_number_install_data',
        categoryKey: 'sensor_number_install_data',
        value: () => s.sensorDigits,
      },
      {
        condition: !!s.edgeDigits && !s.isStreamTrap && !s.isRotaingEquipment,
        headerKey: 'edge_device_number_install_data',
        categoryKey: 'edge_device_number_install_data',
        value: () => s.edgeDigits,
      },
      {
        condition: s.isStreamTrap && info.length > GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
        headerKey: 'inlet_sensor_number_install_data',
        categoryKey: 'inlet_sensor_number_install_data',
        value: () => findInfo('Inlet-S/N'),
      },
      {
        condition: s.isStreamTrap && info.length > GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
        headerKey: 'outlet_sensor_number_install_data',
        categoryKey: 'outlet_sensor_number_install_data',
        value: () => findInfo('Outlet-S/N'),
      },
      {
        condition: s.isStreamTrap && info.length > GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
        headerKey: 'dau_number_install_data',
        categoryKey: 'dau_number_install_data',
        value: () =>
          findInfo('MAC')
            ?.match(/.{1,2}/g)
            ?.join(':'),
      },
      {
        condition: s.isStreamTrap && info.length > GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
        headerKey: 'gateway_number_install_data',
        categoryKey: 'gateway_number_install_data',
        value: () => findInfo('Gateway'),
      },
      {
        condition: !!s.junctionBoxSlot && !s.isStreamTrap && !s.isRotaingEquipment,
        headerKey: 'junction_box_slot_install_data',
        categoryKey: 'junction_box_slot_install_data',
        value: () => s.junctionBoxSlotLabel,
      },
    ];

    const output: {
      header: string;
      items: { text: string; category: string }[];
    }[] = [];

    for (const item of config) {
      if (item.condition) {
        const val = item.value();
        if (val) {
          output.push({
            header: this.i18n.langString(item.headerKey),
            items: [{ text: val, category: this.i18n.langString(item.categoryKey) }],
          });
        }
      }
    }

    return output.length > GLOBAL_CONSTANT.DEFAULT_INITIALIZER ? output : null;
  });
}
